/**
 * The single source of truth for every UI language the app ships.
 *
 * Before this existed the same list was hardcoded in five places — the i18next
 * `supportedLngs`, the header `LanguageSwitcher`, the profile page's
 * `SUPPORTED_LOCALES`, the RTL provider's `RTL_LANGUAGES` set, and the date-fns
 * locale map — and they had already drifted apart from each other. Adding or
 * removing a language is now a one-line edit here.
 *
 * Deliberately dependency-free: `scripts/check-locale-parity.ts` imports this
 * module directly under Node's native type-stripping, which cannot resolve the
 * `@/` alias or bundler-only imports. Keep it that way — the date-fns mapping
 * lives in `date-fns-locale.ts` precisely so this file imports nothing.
 */

export type AppLanguage = {
    /** BCP-47 code. Regional codes are allowed and are matched before their base. */
    readonly code: string;
    /** English name, for `aria-label`s and developer-facing surfaces. */
    readonly label: string;
    /** Endonym — what a speaker of the language calls it. Shown in the picker. */
    readonly nativeLabel: string;
    /** Flag country code for `react-international-phone`'s `FlagImage`. */
    readonly iso2: string;
    readonly rtl: boolean;
};

const ENGLISH: AppLanguage = {
    code: 'en',
    label: 'English',
    nativeLabel: 'English',
    iso2: 'us',
    rtl: false,
};

/** English first (the reference locale), then alphabetical by English name. */
export const LANGUAGES: readonly AppLanguage[] = [
    ENGLISH,
    { code: 'ar', label: 'Arabic', nativeLabel: 'العربية', iso2: 'sa', rtl: true },
    {
        code: 'pt-BR',
        label: 'Brazilian Portuguese',
        nativeLabel: 'Português (Brasil)',
        iso2: 'br',
        rtl: false,
    },
    { code: 'zh', label: 'Chinese', nativeLabel: '中文', iso2: 'cn', rtl: false },
    { code: 'fr', label: 'French', nativeLabel: 'Français', iso2: 'fr', rtl: false },
    { code: 'de', label: 'German', nativeLabel: 'Deutsch', iso2: 'de', rtl: false },
    { code: 'pt', label: 'Portuguese', nativeLabel: 'Português', iso2: 'pt', rtl: false },
    { code: 'es', label: 'Spanish', nativeLabel: 'Español', iso2: 'es', rtl: false },
];

/** The reference locale every other bundle is checked against. */
export const DEFAULT_LANGUAGE = ENGLISH;

export const LANGUAGE_CODES: readonly string[] = LANGUAGES.map((language) => language.code);

/**
 * Maps an arbitrary i18next/browser language tag onto a shipped language.
 *
 * Exact match wins before the base-code fallback, which is what keeps `pt-BR`
 * and `pt` distinct — the previous `code.split('-')[0]` shortcut collapsed
 * every Portuguese tag onto one entry. `pt-PT` correctly lands on `pt`, and
 * anything unsupported lands on English rather than rendering raw keys.
 */
export function resolveLanguage(code: string | null | undefined): AppLanguage {
    const normalized = (code ?? '').trim().toLowerCase();
    if (normalized === '') return DEFAULT_LANGUAGE;

    const exact = LANGUAGES.find((language) => language.code.toLowerCase() === normalized);
    if (exact !== undefined) return exact;

    const base = normalized.split('-')[0];
    if (base === undefined || base === '') return DEFAULT_LANGUAGE;

    return LANGUAGES.find((language) => language.code.toLowerCase() === base) ?? DEFAULT_LANGUAGE;
}
